// @flow

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useI18n } from '../../contexts/I18nContext';
import classNames from 'classnames';
import { REF_STATUSES } from '../../models/Request';

import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

import withStyles from '../../utils/withStyles';
import styles from './Styles/AddTaskDialog.Style';
import AutocompleteProperty from '../Properties/AutocompleteProperty';
import AutocompleteCommonProperty from '../Properties/AutocompleteCommonProperty';
import type { Unit } from '../../services/SPBackend';
import { selectUser, selectDefaultUnit } from '../../reduxStore/modules/user/selectors';

type Props = {
  classes: Object,
  setOpen: Function,
  open: boolean,
  triggerAction: Function
};

// eslint-disable-next-line react/no-multi-comp
const AddTask = (props: Props) => {
  const {
    classes,
    setOpen,
    triggerAction,
    open
  } = props;

  const translate = (key) => {
    return i18n && typeof i18n.t === 'function' ? i18n.t(key) : key;
  };

  // Redux
  const i18n = useI18n();
  const defaultUnit = useSelector(selectDefaultUnit);
  const user = useSelector(selectUser);
  const email = user?.Email || '';
  
  const [selectedUnit, setSelectedUnit] = useState(defaultUnit ? defaultUnit.RoomKey : null);
  const [selectedValue, setSelectedValue] = React.useState(null);
  
  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };
  
  const emptyFormState = {
    User: email,
    Coords: 'FL_RN_R',
    Issue: '',
    RoomKey: defaultUnit ? defaultUnit.RoomKey : '',
    Status: REF_STATUSES.PENDING,
    Summary: ''
  };
  
  const [newRequest, setNewRequest] = useState(emptyFormState);
  
  // Update form state when user or email changes
  useEffect(() => {
    if (email) {
      setNewRequest(prev => ({
        ...prev,
        User: email
      }));
    }
  }, [email]);
  
  // Fix the infinite loop by removing newRequest from dependency array
  // and using functional update pattern
  useEffect(() => {
    if (defaultUnit !== null) {
      setNewRequest(prev => ({
        ...prev,
        RoomKey: defaultUnit.RoomKey
      }));
      setSelectedUnit(defaultUnit);
    }
  }, [defaultUnit]); // Only depend on defaultUnit
  
  const emptyForm = () => {
    setNewRequest({
      ...emptyFormState,
      User: email,
    });
  };

  return (
    <Dialog aria-labelledby='form-add-unit'
      onClose={() => setOpen(false)}
      open={open}
    >
    {
      newRequest 
      ?
      <form
        autoComplete='off'
        className={classNames(
          classes.dialogFormCtr,
          'xlarge'
        )}
        noValidate
        onSubmit={e => {
          e.preventDefault();
          triggerAction(newRequest, selectedUnit);
          emptyForm();
          setOpen(false);
        }}
      >
        <DialogTitle id='form-dialog-title'>{`Add a ${translate('task')}`}</DialogTitle>
        <DialogContent className={classNames(classes.grow, classes.flexContainer)}>
          <div id='createTask'>
            <Input
              autoFocus
              classes={{
                root: classes.secondInput
              }}
              fullWidth
              id='task'
              label={`${translate('Task')} Name`}
              margin='dense'
              onChange={e =>
                setNewRequest({
                  ...newRequest,
                  Issue: e.target.value
                })
              }
              placeholder={`${translate('Task')} Name`}
              type='text'
              value={newRequest.Issue}
            />

          <TextField
              rows='4'
              fullWidth
              id='summary'
              label='Summary'
              margin='dense'
              multiline
              onChange={e =>
                setNewRequest({
                  ...newRequest,
                  Summary: e.target.value
                })
              }
              placeholder='Additional Info'
              type='text'
              value={newRequest.Summary}
            />
            {
              !defaultUnit
              ?
              <div>
                  <br/><br/>
                  <span>{`${translate('Property')} Type`}</span>
                  <br/>
                  <FormControlLabel
                    color= {setSelectedValue === 'common' ? 'primary' : 'default'}
                    control={
                      <Radio
                        checked={selectedValue === 'common'}
                        color= 'primary'
                        inputProps={{ 'aria-label': 'Common' }}
                        name="radio-button-common"
                        onChange={handleChange}
                        value='common'  
                      />
                    }
                    label="Common Area"
                    labelPlacement="end"
                    value="common area"
                  />
                  <FormControlLabel
                    color= {setSelectedValue === 'unit' ? 'primary' : 'default'}
                    control={
                      <Radio
                        checked={selectedValue === 'unit'}
                        color= 'primary'
                        inputProps={{ 'aria-label': 'Unit' }}
                        name="radio-button-unit"
                        onChange={handleChange}
                        value='unit'  
                      />
                      }
                        label="Unit"
                        labelPlacement="end"
                        value="unit"
                  />
                  { selectedValue === 'unit' 
                  ?
                    <div className={classes.secondInput}>
                      <AutocompleteProperty 
                        setUnit={(unit: Unit)=> {
                          setSelectedUnit(unit);
                          setNewRequest({
                            ...newRequest,
                            RoomKey: unit.RoomKey
                          });
                        }}
                      />
                    </div>
                  : selectedValue === 'common'
                    ? 
                      <div className={classes.secondInput}>
                        <AutocompleteCommonProperty 
                          setUnit={(unit: Unit)=> {
                            setSelectedUnit(unit);
                            setNewRequest({
                              ...newRequest,
                              RoomKey: unit.RoomKey
                            });
                          }}
                        />
                      </div>
                    : null
                  }
              </div>
              : null
            }
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            color='primary'
            onClick={() => setOpen(false)}
          >
            {'Cancel'}
          </Button>
          
          <Button
            color='primary'
            disabled={!newRequest.RoomKey || !newRequest.Issue}
            type='submit'
            variant='contained'
          >
            {`Add ${translate('task')}`}
          </Button>

        </DialogActions>
      </form>
    : null
    } 
    </Dialog>
  );
};

export default withStyles(styles)(AddTask);
